import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { QuoteService } from '@app/quoting/services/quote.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { UserRole, UtilityHubIdentity, HasRole } from '@auth/models/utility-hub-identity';
import { Quote, QuoteState } from '@app/quoting/models/quote';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, pluck, tap } from 'rxjs/operators';
import { QuoteRequestState } from '@app/shared';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { UtilityHubConfiguration } from "@configuration/models/utilityhub-configuration";
import { UtilityHubService } from '@app/shared/services/utilityhub.service';
import { MarginTerm } from '@app/quoting/models/quote-request-summary';
import { QuoteHelperService } from '../../services/quote-helper.service';
import { QuoteSettingsService } from '@app/quoting/services/quote-settings.service';
import { SetAgentFee, SetMarginDollar, SetMarginTerm } from '@utility-information/state/actions/utility-information.actions';
import { utilityUOMSelector, contractLengths_Selected, marginTermSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { configurationSelector, isReadOnlySelector, loggedInUtilityHubIdentitySelector, quoteConfigurationSelector, quoteSelector, utilityAccountsSelector } from '@app/state/app.selectors';
import { ConfirmDueDateComponent } from '@app/shared/components/confirm-due-date/confirm-due-date.component';
import { SetQuoteConfiguration, SetQuoteDueDate } from '@quoting/state/actions/quoting-module.actions';
import { MatDialog } from '@angular/material/dialog';
import { QuotationDeactivationCheckerService } from '@app/shared/services/quotation-deactivation-checker/quotation-deactivation-checker.service';
import { ActivatedRoute } from '@angular/router';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { UtilityDetails } from '@app/shared/models/customer-details';
import { UtilityRateCode } from '@app/shared/models/utility-rate-code';
import { QuickQuoteService } from '@app/quick-quote/services/quick-quote.service';
import moment from 'moment';
import { UtilityStartDateModel } from '@quoting/models/utility-start-date';
import { QuoteStateHelperService } from '@app/shared/services/quote-state-helper.service';

export interface QuoteSettings {
  upliftDollar: number;
  marginDollar: number;
  isTaxIncluded: boolean;
  quoteDueDate: string;
}

@Component({
  selector: 'app-quote-settings',
  templateUrl: './quote-settings.component.html',
  styleUrls: ['./quote-settings.component.scss'],
  providers: [QuotationDeactivationCheckerService]
})
export class QuoteSettingsComponent implements OnInit, OnDestroy {
  public commodity = CommodityType;
  public quoteRequestTypeEnum = QuoteRequestType;
  public frmGetQuote: FormGroup;
  public showDetails = true;
  public moreDetails = false;
  public fetchingQuote: boolean;
  public user: UtilityHubIdentity;
  public UserRole = UserRole;
  public hasIDontKnows: boolean;
  public upliftMax: number;
  public upliftMin: number;
  public quoteRequestId: number;

  @Output() fetchQuote = new EventEmitter<QuoteSettings>();
  @Output() isGetQuotePageValid = new EventEmitter<boolean>();
  @Output() marginChanged = new EventEmitter<boolean>();

  @Input() contractLengths;
  @Input() quoteRequestState: QuoteRequestState;
  @Input() showUpliftInvalid: boolean = false;
  @Input() pricingAPICall: boolean;

  public quote: Quote;

  route$: Subscription;
  selectLoggedInUtilityHubIdentity$: Subscription;
  selectBrokerageConfiguration$: Subscription;
  upliftDollarInput$: Subscription;
  marginDollarInput$: Subscription;
  agentFeeInput$: Subscription;
  annualLoadFactorObs: Observable<string>;
  pricingCurveDateObs: Observable<Date>;
  loadObs$: Subscription;

  public quoteStateType = QuoteState;
  public frmQuoteConfiguration: FormGroup;
  brokerageConfig: UtilityHubConfiguration;
  upliftDollar$: Subscription;
  quickQuoteUtilityAccountNumber: string= 'xxx'; 
  quoteRequestType: string;
  hasIDontKnows$: Subscription;
  quoteRequestType$: Subscription;
  loadFactorUpdated$: Subscription;
  pricingCurveDateDisplayed$: Subscription;
  selectBrokerageConfigurationObs$: Observable<UtilityHubConfiguration>;
  public pricingSource: PricingSource;
  public PricingSourceEnum = PricingSource;
  utilityUOM$: Observable<string>;
  isButtonQuoteDisabled: boolean;
  selectQuote$: Subscription;
  contractLengths$: any;
  marginTermResults: MarginTerm[];
  marginTerm: Array<MarginTerm> = [];
  marginDollarOriginal: number;
  marginDollarPreviousInput: number;
  agentFeePreviousInput: number;
  brokerFeePreviousInput: number;
  isTaxIncluded$: Subscription;
  customerIsTaxExempt$: Subscription;
  isPowwrSupplier: boolean;
  updateQuoteDueDate$: Subscription;
  newContractStartDates: string[];
  quoteWithConfiguration$: Subscription;
  stateCode: string;
  utilityAccountNumber: string;
  commodityType = CommodityType;
  utilityDetails: UtilityDetails;
  utilityRateCode: UtilityRateCode;
  shouldLoadProfileBeValidated: boolean;
  dunsLookupEnabled: boolean;
  newContractStartMonth = new FormControl(moment(), Validators.required);
  isQuickQuoteJourneyCongfigured: boolean;
  isReadOnly: boolean;

  get brokerFeeCtrl() {
    return this.frmGetQuote?.get('upliftDollar');
  }
  get isTaxIncluded() {
    return this.frmGetQuote?.get('isTaxIncluded');
  }
  get marginDollarCtrl(): AbstractControl {
    return this.frmGetQuote.get('marginDollar');
  }
  get agentFeeCtrl() {
    return this.frmGetQuote?.get('agentFee');
  }

  get areUpliftLimitsSet(): boolean {
    return this.upliftMin !== undefined && this.upliftMax !== undefined;
  }

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private quoteService: QuoteService,
    private utilityHubService: UtilityHubService,
    private pricingSourceService: PricingSourceService,
    public quoteHelperService: QuoteHelperService,
    private dialog: MatDialog,
    public quotationDeactivationCheckerService: QuotationDeactivationCheckerService,
    private route: ActivatedRoute,
    public quoteRequestService: QuoteRequestService,
    public quickQuoteService: QuickQuoteService,
    public quoteStateHelperService: QuoteStateHelperService,
    private quoteSettingsService: QuoteSettingsService,
  ) {
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
  }

  ngOnDestroy(): void {
    this.selectLoggedInUtilityHubIdentity$?.unsubscribe();
    this.selectBrokerageConfiguration$?.unsubscribe();
    this.upliftDollarInput$?.unsubscribe();
    this.marginDollarInput$?.unsubscribe();
    this.agentFeeInput$.unsubscribe();
    this.hasIDontKnows$?.unsubscribe();
    this.quoteRequestType$?.unsubscribe();
    this.selectQuote$?.unsubscribe();
    this.contractLengths$?.unsubscribe();
    this.upliftDollar$?.unsubscribe();
    this.loadFactorUpdated$?.unsubscribe();
    this.pricingCurveDateDisplayed$?.unsubscribe();
    this.customerIsTaxExempt$?.unsubscribe();
    this.route$?.unsubscribe();
    this.quoteWithConfiguration$?.unsubscribe();
    this.updateQuoteDueDate$?.unsubscribe();
    this.loadObs$?.unsubscribe();
    if (this.quoteRequestId && this.frmGetQuote) {
      this.quoteSettingsService.updateQuoteSettings(this.quoteRequestId, {
        upliftDollar: this.frmGetQuote.get('upliftDollar')?.value,
        marginDollar: this.frmGetQuote.get('marginDollar')?.value,
        isTaxIncluded: this.frmGetQuote.get('isTaxIncluded')?.value,
        quoteDueDate: this.quoteDueDateCtrl,
        agentFee: this.frmGetQuote.get('agentFee')?.value,
      });
    }
  }

  ngOnInit(): void {

    this.initializeForm();

    this.showDetails = true;

    this.customerIsTaxExempt$ = this.quoteService.quoteUpdated.pipe(pluck('customerIsTaxExempt')).subscribe((customerIsTaxExempt) => {

      if (customerIsTaxExempt) {
        this.frmGetQuote?.get('isTaxIncluded').patchValue(false);
        this.frmGetQuote?.get('isTaxIncluded').disable();
      }
      else {
        this.isTaxIncluded$ = this.quoteService.quoteUpdated.pipe(pluck('isTaxIncluded')).subscribe((x) => {
          this.frmGetQuote?.get('isTaxIncluded').patchValue(x);
        });
      }

    });

    
    this.store.select(configurationSelector).subscribe((c) => {
      this.isQuickQuoteJourneyCongfigured = c.configuration?.quoteTypes?.quickQuoteJourneyEnabled;
      this.shouldLoadProfileBeValidated = c.configuration?.quoteTypes?.quickQuoteJourneyRequireLoadProfile;
      this.dunsLookupEnabled = c.supplierConfiguration.dunsLookupEnabled;
    });

    this.upliftDollarInput$ = this.quoteService.quoteUpdated.pipe(pluck('upliftDollar')).subscribe((x) => {
      this.frmGetQuote?.get('upliftDollar').patchValue(x);
    });

    this.marginDollarInput$ = this.quoteService.quoteUpdated.pipe(pluck('marginDollar')).subscribe((x) => {
      this.frmGetQuote?.get('marginDollar').patchValue(x);
    });

    this.agentFeeInput$ = this.quoteService.quoteUpdated.pipe(pluck('agentFee')).subscribe((x) => {
      this.frmGetQuote?.get('agentFee').patchValue(x);
    });

    this.loadFactorUpdated$ = this.quoteService.loadFactorUpdated.subscribe((x) => {
      this.quote = x;
      this.setAnnualLoadFactor();
    });

    this.pricingCurveDateDisplayed$ = this.quoteService.pricingCurveDate.subscribe((x) => {
      this.quote = x;
      this.setPricingCurveDate();
    });

    this.hasIDontKnows$ = this.quoteService.quoteRequestStateUpdated.subscribe((x) => {
      this.hasIDontKnows = x.hasIDontKnows;
    });

    this.quoteWithConfiguration$ = combineLatest([
      this.store.select(quoteSelector),
      this.store.select(utilityAccountsSelector),
      this.store.select(quoteConfigurationSelector),
      this.store.select(isReadOnlySelector)
    ]).pipe(
      map(([quotingModuleState, utilityAccounts, quoteConfiguration, isReadOnly]) => {
        this.updateQuoteButtonState(quotingModuleState.quote);
        this.newContractStartDates = this.quoteHelperService.determineContractStartDates(quotingModuleState.quote, quoteConfiguration, utilityAccounts)
        this.isReadOnly = isReadOnly;

        if (this.isReadOnly) {
          this.frmGetQuote.disable({ emitEvent: false });
        }
      })
    ).subscribe();

    this.selectLoggedInUtilityHubIdentity$ = this.store
      .select(loggedInUtilityHubIdentitySelector)
      .pipe(
        tap((x) => (this.user = x)),
      )
      .subscribe();

    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
      }
    });

    this.quoteService.quoteUpdated.subscribe(() => {
      if (!this.quoteSettingsService.pendingRestore) return;
      this.quoteSettingsService.pendingRestore = false;
      const saved = this.quoteSettingsService.quoteSettings;
      if (saved && this.quoteSettingsService.quoteSettingsQuoteRequestId === this.quoteRequestId) {
        this.frmGetQuote?.patchValue({
          upliftDollar: saved.upliftDollar,
          marginDollar: saved.marginDollar,
          isTaxIncluded: saved.isTaxIncluded,
          agentFee: saved.agentFee,
        });
      }
    });

    this.selectQuote$ = this.store.select(quoteSelector).subscribe((s) => {
      if (s.quoteConfiguration != null){
      this.stateCode = s.quoteConfiguration.zipLookup.state;
      this.utilityAccountNumber = s.quoteConfiguration.zipLookup.utilityAccountNumber;
      this.utilityDetails = s.quoteConfiguration.utilityDetails;
      this.utilityRateCode = s.quoteConfiguration.loadProfile.loadProfile;
      this.commodity = s.quoteConfiguration.commodityType;
      this.newContractStartMonth.setValue(s.quoteConfiguration.newContractStartMonth);
      this.quote = s;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteRequestState']?.currentValue) {
      this.quoteRequestState = changes['quoteRequestState'].currentValue;
      this.quoteRequestType = this.quoteRequestState.quoteRequestType;
      this.processPipelines();
    }
    if (changes?.contractLengths) this.contractLengths = changes?.contractLengths.currentValue;
    if (changes['pricingAPICall']?.currentValue) {
      this.pricingAPICall = changes?.pricingAPICall.currentValue;
    }
  }

  private updateQuoteButtonState(quote: Quote): void {
    if (this.quoteHelperService.CanEdit(quote, this.user)) {
      this.isButtonQuoteDisabled = false;
      this.frmGetQuote?.enable();
      this.marginDollarOriginal = this.marginDollarCtrl.value;
    } else {
      this.isButtonQuoteDisabled = true;
      this.frmGetQuote?.disable();
    }
  }

  private processPipelines() {
    if (this.quoteRequestType) {
      if (!this.quoteRequestState.createdBySupplier && this.user.isSupplierManagerOrSalesManager && this.quoteRequestState.ownerOrganisationExternalId.endsWith('B')) {
        this.showUpliftInvalid = true;
        this.selectBrokerageConfigurationObs$ = this.utilityHubService.getBrokerageConfiguration(
          this.quoteRequestState.ownerOrganisationExternalId
        );
      } else {
        this.selectBrokerageConfigurationObs$ = this.store.select(configurationSelector);
      }

      this.store.select(quoteSelector).subscribe((q) => {
        this.quote = q?.quote;
      });

      this.selectBrokerageConfiguration$ = this.selectBrokerageConfigurationObs$
        .pipe(
          tap((x) => (this.brokerageConfig = x)),
          tap((x) => this.setUpliftValues(x)),
          tap((s) => this.isPowwrSupplier = s.supplierConfiguration.pricing.powwrSupplier && this.quote.quoteRequestState.commodity !== CommodityType.Gas)
        )
        .subscribe();

      this.quoteRequestType$ = this.quoteService.quoteRequestStateUpdated
        .pipe(
          tap((x) => {
            this.pricingSourceService.getPricingSource(x.quoteRequestType, x.commodity).subscribe((ps) => {
              this.pricingSource = ps.source;
            });
            return (this.quoteRequestType = x.quoteRequestType as string);
          }),
          tap((x) => this.setAnnualLoadFactor()),
          tap((x) => this.setPricingCurveDate())
        )
        .subscribe();

      const upliftCtrl = this.frmGetQuote.get('upliftDollar');
      upliftCtrl.updateValueAndValidity();
      this.frmGetQuote.updateValueAndValidity();
    }
  }

  marginDollarChanges(marginElement: HTMLSelectElement) {
    let margin = Number(this.isEmptyElementChecker(marginElement, Number(marginElement?.value), this.marginDollarPreviousInput, "Margin"));
    if (margin != this.marginDollarPreviousInput) {
    this.store.select(marginTermSelector).subscribe((x) => {
      this.marginTerm = x;
    }).unsubscribe();
    let termMarginResult = this.marginTerm.map(obj => {
        return { ...obj, margin: margin };
    });
    this.store.dispatch(new SetMarginTerm(this.quote?.quoteRequestState?.quoteRequestId.toString(), termMarginResult));
    this.store.dispatch(new SetMarginDollar(this.quote?.quoteRequestState?.quoteRequestId.toString(), margin));
      this.marginChanged.emit(margin == Number(this.marginDollarOriginal) ? false : true);
    }
  }

  onChangeAgentFee(agentFeeElement: HTMLSelectElement) {
    let agentFee = Number(this.isEmptyElementChecker(agentFeeElement, Number(agentFeeElement?.value), this.agentFeePreviousInput, "Agent"));
    this.store.dispatch(new SetAgentFee(this.quote?.quoteRequestState?.quoteRequestId.toString(), agentFee));
  }

  onChangeBrokerFee(brokerFeeElement: HTMLSelectElement) {
    let brokerFee = Number(this.isEmptyElementChecker(brokerFeeElement, Number(brokerFeeElement?.value), this.brokerFeePreviousInput, "Broker"));
  }

  isEmptyElementChecker(element: HTMLSelectElement, currentValue: Number, previousValue: Number, frmElementName: String) {
    if (element?.value?.trim() == "") {
      if (frmElementName == "Margin") {
        this.marginDollarCtrl.setValue(element.value = String(previousValue));
      }
      else if (frmElementName == "Agent") {
        this.agentFeeCtrl.setValue(element.value = String(previousValue));
      }
      else if (frmElementName == "Broker") {
        this.brokerFeeCtrl.setValue(element.value = String(previousValue));
      }
      this.frmGetQuote.updateValueAndValidity();
      return previousValue;
    }
    return currentValue;
  }

  isGasQuote(): boolean {
    return this.quoteRequestState && this.quoteRequestState.commodity == CommodityType.Gas;
  }

  patchValues(s: any) {
    this.frmGetQuote?.patchValue(s);
  }

  initializeForm() {
    this.frmGetQuote = this.fb.group({
      upliftDollar: ['0.00'],
      marginDollar: ['0.00'],
      isTaxIncluded: [true],
      agentFee: ['0.00'],
      utilityRateCode: [this.utilityRateCode, Validators.required],
      newContractStartMonth: [this.newContractStartMonth],
    });
  }

  setFormValidators() {
    const validators = [Validators.pattern(/^\d{0,8}\.?\d{0,10}$/), Validators.required];
    const negativeValidators = [Validators.pattern(/^-?\d{0,8}\.?\d{0,10}$/), Validators.required];
    this.frmGetQuote.get('marginDollar').setValidators(negativeValidators);
    validators.push(Validators.min(this.upliftMin));
    validators.push(Validators.max(this.upliftMax));
    this.frmGetQuote.get('agentFee').setValidators(validators);
    this.frmGetQuote.get('upliftDollar').setValidators(validators);

    this.frmGetQuote.markAllAsTouched();
    this.frmGetQuote.updateValueAndValidity();
  }

  setUpliftValues(brokerageConfig: UtilityHubConfiguration) {
    this.upliftMax = +(brokerageConfig?.configuration.customUpliftThreshold.maximum ?? this.upliftMax);
    this.upliftMin = +(brokerageConfig?.configuration.customUpliftThreshold.minimum ?? this.upliftMin);

    this.setFormValidators();
  }

  public get quoteDueDateCtrl() {
    let quoteDueDate = null
    this.updateQuoteDueDate$ = this.store.select(quoteSelector).subscribe((x) => {
      let quote = x;
      quoteDueDate = quote.quoteDueDate;
      }
    );

    return quoteDueDate;
  }

  public getQuoteDueDateUpdate(): void {
    const dateToUse = new Date();
    const dialogRef = this.dialog.open(ConfirmDueDateComponent, {
      width: '30%',
      data: {
        title: `Quote Due Date`,
        message: 'Would you like to update the due date for this quote?',
        primaryButtonText: 'Use today\'s date',
        secondaryButtonText: 'Update due date',
        quoteDueDateToolTip: 'Quote Due Date',
        todaysDate: new Date(Date.UTC(dateToUse.getFullYear(), dateToUse.getMonth(), dateToUse.getDate(), 23, 59, 59, 0)),
      },
    });

    dialogRef.afterClosed().subscribe((isUseDefault) => {

      if (isUseDefault) {
        let dateToUse = new Date();
        this.store.dispatch(new SetQuoteDueDate(new Date(Date.UTC(dateToUse.getFullYear(), dateToUse.getMonth(), dateToUse.getDate(), 23, 59, 59, 0))));
      }

      if (isUseDefault !== '' && typeof isUseDefault != 'undefined')
        this.getQuote();

    });
  }

  saveRateCodes(): Promise<void> {
    return new Promise((resolve, reject) => {
        const quoteRequestId = this.quote?.quoteRequestState?.quoteRequestId.toString();
        const rateCode = this.frmGetQuote.get('utilityRateCode').value.utilityRateCode.rateCode
        const rateCodes = [{
          utilityAccountNumber: this.quickQuoteUtilityAccountNumber,
          rateCode: rateCode,
          rateClass: rateCode
        }];
        this.quoteRequestService.saveRateCodes(quoteRequestId, rateCodes).toPromise()
          .then(response => {
            resolve();
          })
          .catch(error => {
            reject(error);
          });
    });
  }

  SaveContractStartMonth(): Promise<void> {
    return new Promise((resolve, reject) => {
      const quoteRequestId = this.quote?.quoteRequestState?.quoteRequestId.toString();
      const newContractStartMonth = new Date(this.frmGetQuote.get('newContractStartMonth').value.newContractStartMonth);
      const newContractStartMonthDetails: UtilityStartDateModel[] = [{
        utilityAccountNumber: this.quickQuoteUtilityAccountNumber,
        contractStartDate: newContractStartMonth
      }];
      
      if (!isNaN(newContractStartMonth.getTime())) {
        this.quoteRequestService.saveNewContractStartMonth(quoteRequestId, newContractStartMonthDetails).toPromise()
          .then(response => {
            this.loadObs$ = forkJoin({
              quoteConfiguration: this.quickQuoteService.getQuoteConfiguration(this.quoteRequestId)
            }).pipe(
              map((result) => {
                this.store.dispatch(new SetQuoteConfiguration(this.quoteRequestId, result.quoteConfiguration));
                resolve();
              })
            ).subscribe();
          })
          .catch(error => {
            reject(error);
          });
      } else {
        resolve();
      }
    });
  }

  async getQuote(): Promise<void> {
    this.frmGetQuote.updateValueAndValidity();

    if(this.moreDetails && this.quote?.quoteRequestState?.quoteRequestType.replace(' ', '') === QuoteRequestType.QuickQuote){
      await this.saveRateCodes();
      await this.SaveContractStartMonth();
    }

    if (!this.frmGetQuote.get('upliftDollar').errors && !this.frmGetQuote.get('agentFee').errors) {
      this.setFormValid();

      const quoteSettings = {
        ...this.frmGetQuote.getRawValue(),
        quoteDueDate: this.quoteDueDateCtrl,
        upliftDollar: this.frmGetQuote.get('upliftDollar').value,
        marginDollar: this.frmGetQuote.get('marginDollar').value,
        isTaxIncluded: this.frmGetQuote.get('isTaxIncluded').value,
        agentFee: this.frmGetQuote.get('agentFee').value,
      };

      this.fetchQuote.emit(quoteSettings);
      this.marginChanged.emit(false);

      if (this.marginDollarCtrl.value !=0)
      {
        this.marginDollarCtrl.disable();
      }
      else
      {
        this.marginDollarCtrl.enable();
      }
    } 
  }

  public hasRole(userRole: UserRole) {
    return HasRole(this.user, userRole);
  }

  public isFormInvalid() {
    if (this.showUpliftInvalid || (this.user?.isBroker ?? true)) {
      const upliftDollarCtrl = this.frmGetQuote?.get('upliftDollar');

      let isUpliftRangeValid = upliftDollarCtrl?.value >= this.upliftMin && upliftDollarCtrl?.value <= this.upliftMax;

      this.isGetQuotePageValid.emit(isUpliftRangeValid);
      if (!isUpliftRangeValid || upliftDollarCtrl?.value == null) {
        isUpliftRangeValid = false;
        this.setFormInvalid();
      }
      return !isUpliftRangeValid;
    }
    return false;
  }

  private setFormValid() {
    const upliftDollarCtrl = this.frmGetQuote?.get('upliftDollar');
    upliftDollarCtrl.setErrors(null);
    upliftDollarCtrl.markAsPristine();
  }

  private setFormInvalid() {
    const upliftDollarCtrl = this.frmGetQuote?.get('upliftDollar');
    const errors = {};
    if (upliftDollarCtrl?.value == null) {
      errors['required'] = true;
    }
    errors['outOfRange'] = true;
    upliftDollarCtrl.setErrors(errors);
    upliftDollarCtrl.markAllAsTouched();
  }

  public isQuoteButtonDisabled() {

    if (this.pricingAPICall) return true;

    let isDisabled = !this.quoteHelperService.CanQuote(this.quote, this.user, this.brokerageConfig?.configuration, this.frmGetQuote.get('upliftDollar')?.errors?.value, this.newContractStartDates);
    return isDisabled ?? this.hasIDontKnows;
  }

  showMoreOrLess(value: boolean) {
    this.moreDetails = value;
  }

  public setAnnualLoadFactor() {
    // check we won't be dividing by zero
    if (this.quote?.prices[0] && this.quote?.prices[0]?.details?.reduce((accum, item) => accum + item.plc, 0) > 0) {
      this.annualLoadFactorObs = of(
        (
          (this.quote.prices[0]?.details?.reduce((accum, item) => accum + item.annualLoad, 0) /
            (8760 * this.quote.prices[0]?.details?.reduce((accum, item) => accum + item.plc, 0))) *
          100
        ).toFixed(0)
      );
    } else {
      this.annualLoadFactorObs = of('0');
    }
  }

  public setPricingCurveDate() {
    if (this.quote && this.quote?.pricingCurveDate) {
        this.pricingCurveDateObs = of(this.quote?.pricingCurveDate);
    }
  }

  public isSupplierManager() {
    return this.user?.currentOrganisation?.role === UserRole.SupplierManager;
  }

  public getQuoteExpirationDisclaimer() {
    let doesQuoteHavePrices = this.quote?.prices.length > 0;
    let result = "";
    if (doesQuoteHavePrices && this.quote?.quoteState === QuoteState.APPROVAL_REQUIRED) {
      result = this.quotationDeactivationCheckerService.GetQuoteExpirationDisclaimerBasedOnQuoteStatus;
    } else {
      result = "";
    }
    return result;
  }
  
}
